import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Edit, Trash2, LogOut, X, Palette, Users, ShoppingBag, Truck, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { MultiImageUpload } from "@/components/ImageUpload";
import { resolveImage } from "@/lib/siteDefaults";

const EMPTY = {
  name: "",
  description: "",
  category: "gym",
  price: 0,
  currency: "usd",
  images: [],
  sizes: [],
  colors: [],
  active: true,
  featured: false,
  stock: "",
  stock_by_size: {},
  weight_oz: "",
};

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product being edited or null
  const [form, setForm] = useState(EMPTY);
  const [imagesText, setImagesText] = useState("");
  const [sizesText, setSizesText] = useState("");
  const [colorsText, setColorsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [priveIds, setPriveIds] = useState([]);
  const [prive, setPrive] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
      api.get("/admin/community/prive/qualifying").then(({ data: q }) => setPriveIds(q.qualifying_product_ids || [])).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("jenova_admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("jenova_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setImagesText("");
    setSizesText("");
    setColorsText("");
    setPrive(false);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      category: p.category,
      price: p.price,
      currency: p.currency || "usd",
      images: p.images || [],
      sizes: p.sizes || [],
      colors: p.colors || [],
      active: p.active,
      featured: p.featured,
      stock: p.stock == null ? "" : String(p.stock),
      stock_by_size: Object.fromEntries(
        Object.entries(p.stock_by_size || {}).map(([k, v]) => [k, v == null ? "" : String(v)])
      ),
      weight_oz: p.weight_oz == null ? "" : String(p.weight_oz),
    });
    setImagesText((p.images || []).join("\n"));
    setSizesText((p.sizes || []).join(", "));
    setColorsText((p.colors || []).join(", "));
    setPrive(priveIds.includes(p.id));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price) {
      toast.error("Name and price are required");
      return;
    }
    setSaving(true);
    const stockRaw = String(form.stock ?? "").trim();
    const weightRaw = String(form.weight_oz ?? "").trim();
    const parsedSizes = sizesText.split(",").map((s) => s.trim()).filter(Boolean);
    // Build per-size stock map from form.stock_by_size, keeping only sizes that are still listed
    // AND have a non-blank numeric value. Blank/missing values = untracked for that size.
    const stockBySize = {};
    for (const s of parsedSizes) {
      const raw = String(form.stock_by_size?.[s] ?? "").trim();
      if (raw === "") continue;
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) stockBySize[s] = Math.max(0, n);
    }
    const hasSizes = parsedSizes.length > 0;
    const payload = {
      ...form,
      price: parseFloat(form.price),
      images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      sizes: parsedSizes,
      colors: colorsText.split(",").map((s) => s.trim()).filter(Boolean),
      // Top-level stock only applies when there are no sizes.
      stock: hasSizes ? null : (stockRaw === "" ? null : Math.max(0, parseInt(stockRaw, 10) || 0)),
      // Per-size stock only applies when sizes exist and at least one is tracked.
      stock_by_size: hasSizes && Object.keys(stockBySize).length > 0 ? stockBySize : null,
      weight_oz: weightRaw === "" ? null : Math.max(0, parseFloat(weightRaw) || 0),
    };
    try {
      let pid = editing?.id;
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/admin/products", payload);
        pid = data.id;
        toast.success("Product created");
      }
      if (prive !== priveIds.includes(pid)) {
        await api.put(`/admin/community/prive/qualifying/${pid}`, { qualifying: prive });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("jenova_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between border-b border-[#E5E2DC] pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Studio</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">
              Product <em className="italic text-stone-600">control</em>.
            </h1>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <span className="text-[#1A1C1A] border-b-2 border-[#1A1C1A] pb-1">Products</span>
              <Link data-testid="admin-nav-site" to="/admin/site" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Palette strokeWidth={1.5} className="w-4 h-4" /> Site Content
              </Link>
              <Link data-testid="admin-nav-founding" to="/admin/founding" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Users strokeWidth={1.5} className="w-4 h-4" /> Founding Members
              </Link>
              <Link data-testid="admin-nav-orders" to="/admin/orders" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <ShoppingBag strokeWidth={1.5} className="w-4 h-4" /> Orders
              </Link>
              <Link data-testid="admin-nav-shipping" to="/admin/shipping-settings" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Truck strokeWidth={1.5} className="w-4 h-4" /> Shipping
              </Link>
              <Link data-testid="admin-nav-broadcast" to="/admin/broadcast" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Send strokeWidth={1.5} className="w-4 h-4" /> Broadcast
              </Link>
              <Link data-testid="admin-nav-reviews" to="/admin/reviews" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Star strokeWidth={1.5} className="w-4 h-4" /> Reviews
              </Link>
              <Link data-testid="admin-nav-community" to="/admin/community" className="text-stone-500 hover:text-[#1A1C1A] pb-1 inline-flex items-center gap-1.5">
                <Users strokeWidth={1.5} className="w-4 h-4" /> Community
              </Link>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid={ADMIN.addProductButton} onClick={openCreate} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Product
            </Button>
            <Button data-testid={ADMIN.logoutButton} onClick={logout} variant="outline" className="rounded-full">
              <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Log Out
            </Button>
          </div>
        </div>

        <div className="mt-8 bg-white border border-[#E5E2DC] rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E5E2DC] bg-[#F9F9F6] text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">
            <div className="col-span-5">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1">Price</div>
            <div className="col-span-1">Stock</div>
            <div className="col-span-1">Featured</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading && <div className="p-8 text-center text-stone-500">Loading…</div>}
          {!loading && products.length === 0 && (
            <div className="p-12 text-center text-stone-500 font-serif italic">No products yet. Add your first.</div>
          )}
          {!loading &&
            products.map((p) => (
              <div key={p.id} data-testid={ADMIN.productRow} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E5E2DC] items-center hover:bg-[#F9F9F6] transition-colors">
                <div className="col-span-5 flex items-center gap-3">
                  <div className="w-12 h-14 bg-[#F4F1EA] rounded overflow-hidden flex-shrink-0">
                    {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <p className="font-serif text-lg leading-tight">{p.name}</p>
                    <p className="text-xs text-stone-500 truncate max-w-md">{p.description}</p>
                  </div>
                </div>
                <div className="col-span-2 text-sm text-stone-700 capitalize">{p.category}</div>
                <div className="col-span-1 text-sm">${p.price.toFixed(2)}</div>
                <div className="col-span-1 text-sm">
                  {(() => {
                    const sbs = p.stock_by_size;
                    if (sbs && Object.keys(sbs).length > 0) {
                      const total = Object.values(sbs).reduce((a, b) => a + (Number(b) || 0), 0);
                      const allZero = Object.values(sbs).every((v) => Number(v) === 0);
                      if (allZero) return <span className="text-[#8B3A3A] font-semibold">Sold out</span>;
                      if (total <= 3) return <span className="text-[#8B6A1A] font-semibold">{total} left</span>;
                      return <span title={Object.entries(sbs).map(([k, v]) => `${k}: ${v}`).join(", ")}>{total}</span>;
                    }
                    if (p.stock == null) return <span className="text-stone-400">∞</span>;
                    if (p.stock === 0) return <span className="text-[#8B3A3A] font-semibold">Sold out</span>;
                    if (p.stock <= 3) return <span className="text-[#8B6A1A] font-semibold">{p.stock} left</span>;
                    return <span>{p.stock}</span>;
                  })()}
                </div>
                <div className="col-span-1 text-sm">
                  {p.featured ? "★" : "—"}
                  {priveIds.includes(p.id) && (
                    <span data-testid={`admin-product-prive-badge-${p.id}`} className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A8865A] font-semibold">Privé</span>
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <Button data-testid={ADMIN.editProductButton} onClick={() => openEdit(p)} variant="outline" size="sm" className="rounded-full">
                    <Edit className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button data-testid={ADMIN.deleteProductButton} onClick={() => del(p)} variant="outline" size="sm" className="rounded-full text-[#8B3A3A] border-[#8B3A3A]/40 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label>Name</Label>
              <Input
                data-testid={ADMIN.productNameInput}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Renewal Performance Legging"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                data-testid={ADMIN.productDescInput}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid={ADMIN.productCategoryInput}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gym">Performance (gym)</SelectItem>
                    <SelectItem value="beauty">Beauty & Hair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (USD)</Label>
                <Input
                  data-testid={ADMIN.productPriceInput}
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Product images</Label>
              <div className="mt-2">
                <MultiImageUpload
                  value={imagesText.split("\n").map((s) => s.trim()).filter(Boolean)}
                  onChange={(urls) => setImagesText(urls.join("\n"))}
                  testId={ADMIN.productImagesInput}
                />
              </div>
              <details className="mt-2">
                <summary className="text-xs text-stone-500 cursor-pointer">Or paste URLs (one per line)</summary>
                <Textarea
                  value={imagesText}
                  onChange={(e) => setImagesText(e.target.value)}
                  rows={2}
                  className="mt-2"
                  placeholder="https://…"
                />
              </details>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sizes (comma-separated)</Label>
                <Input
                  data-testid={ADMIN.productSizesInput}
                  value={sizesText}
                  onChange={(e) => setSizesText(e.target.value)}
                  placeholder="XS, S, M, L, XL"
                />
              </div>
              <div>
                <Label>Colors (comma-separated)</Label>
                <Input
                  data-testid={ADMIN.productColorsInput}
                  value={colorsText}
                  onChange={(e) => setColorsText(e.target.value)}
                  placeholder="Stone, Onyx"
                />
              </div>
            </div>

            {(() => {
              const parsedSizes = sizesText.split(",").map((s) => s.trim()).filter(Boolean);
              if (parsedSizes.length === 0) {
                // No sizes → single stock field
                return (
                  <div>
                    <Label>
                      Stock quantity
                      <span className="ml-1 text-stone-400 font-normal text-xs">(blank = unlimited)</span>
                    </Label>
                    <Input
                      data-testid="admin-product-stock-input"
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={form.stock}
                      onChange={(e) => setForm({ ...form, stock: e.target.value })}
                      placeholder="e.g. 12"
                      className="max-w-xs"
                    />
                  </div>
                );
              }
              // Sizes present → per-size stock inputs
              return (
                <div className="bg-[#F9F7F2] border border-[#E5E2DC] rounded-xl p-4">
                  <div className="flex items-baseline justify-between mb-3">
                    <Label className="text-sm font-semibold">Stock by size</Label>
                    <span className="text-xs text-stone-500 font-normal">blank = unlimited for that size</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {parsedSizes.map((s) => (
                      <div key={s}>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-1 text-center">
                          {s}
                        </div>
                        <Input
                          data-testid={`admin-product-stock-size-${s}`}
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={form.stock_by_size?.[s] ?? ""}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              stock_by_size: { ...(form.stock_by_size || {}), [s]: e.target.value },
                            })
                          }
                          placeholder="—"
                          className="text-center"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div>
              <Label>
                Shipping weight per unit (oz)
                <span className="ml-1 text-stone-400 font-normal text-xs">blank = use default from Shipping Settings</span>
              </Label>
              <Input
                data-testid="admin-product-weight-input"
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={form.weight_oz}
                onChange={(e) => setForm({ ...form, weight_oz: e.target.value })}
                placeholder="e.g. 8 (for an 8 oz item)"
                className="max-w-xs"
              />
            </div>
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <Switch
                  data-testid={ADMIN.productFeaturedInput}
                  checked={form.featured}
                  onCheckedChange={(v) => setForm({ ...form, featured: v })}
                />
                <Label>Featured on homepage</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label>Active (visible in shop)</Label>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#FBF5EE] border border-[#E8D9C4] rounded-xl p-4">
              <Switch data-testid="admin-product-prive-switch" checked={prive} onCheckedChange={setPrive} />
              <div>
                <Label>Privé qualifying set</Label>
                <p className="text-xs text-stone-500 mt-1">Buyers of this product get the Privé Instagram channel email and can be verified for Privé.</p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid={ADMIN.cancelProductButton} onClick={() => setDialogOpen(false)} variant="outline" className="rounded-full">
              Cancel
            </Button>
            <Button data-testid={ADMIN.saveProductButton} onClick={save} disabled={saving} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
